import type { ReactNode } from 'react'
import { SplitViewHeaderSearch } from '@/components/SplitView/SplitViewHeader'
import {
  SplitViewSidebarListItem,
  SplitViewSidebarListItemTitle,
  SplitViewSidebarListItemArrow,
  SplitViewSidebarListItemSubtitle,
} from '@/components/SplitView/SplitViewSidebarListItem'
import styles from './OCRScopingJobsListHeader.module.css'
import { IconButton } from '@/components/IconButton'
import { Checkbox } from '@/components/Checkbox'

export interface OCRScopingJobsListHeaderProps {
  children?: ReactNode
}

export function OCRScopingJobsListHeader({
  children,
}: OCRScopingJobsListHeaderProps) {
  return <div className={styles.root}>{children}</div>
}

export interface OCRScopingJobsListHeaderSearchProps {
  showSearchField: boolean
  statusFilterTitle?: string
  statusFilterCount?: number
  searchValue: string
  onSearchChange: (value: string) => unknown
  onBack: () => unknown
  onToggleSearchField: () => unknown
}

export function OCRScopingJobsListHeaderSearch({
  showSearchField,
  statusFilterTitle,
  statusFilterCount,
  searchValue,
  onSearchChange,
  onBack,
  onToggleSearchField,
}: OCRScopingJobsListHeaderSearchProps) {
  return (
    <div className={styles.searchContainer}>
      {!showSearchField && (
        <div className={styles.searchLeftSide}>
          <IconButton name="back" label="Back" onPress={onBack} />
          <div className={styles.searchTitle}>
            {statusFilterTitle}
            <span className={styles.searchTitleCount}>
              ({statusFilterCount})
            </span>
          </div>
        </div>
      )}
      {showSearchField && (
        <SplitViewHeaderSearch
          onChange={onSearchChange}
          value={searchValue}
          placeholder="Search jobs"
        />
      )}
      <IconButton
        name={showSearchField ? 'close' : 'search'}
        label={showSearchField ? 'Close search' : 'Search'}
        onPress={onToggleSearchField}
      />
    </div>
  )
}

export interface OCRScopingJobsListHeaderWorkspaceFilterProps {
  canManageOCRScopingJobs: boolean
  workspaceSelector: ReactNode
}

export function OCRScopingJobsListHeaderWorkspaceFilter({
  canManageOCRScopingJobs,
  workspaceSelector,
}: OCRScopingJobsListHeaderWorkspaceFilterProps) {
  return (
    <div
      className={
        canManageOCRScopingJobs
          ? styles.searchWithWorkspaceFilterContainer
          : styles.searchWithOutWorkspaceFilterContainer
      }
    >
      {canManageOCRScopingJobs ? workspaceSelector : <span>OCR job scope</span>}
    </div>
  )
}

export interface OCRScopingJobsListHeaderItemColumnsProps {
  showCheckbox: boolean
  checked: boolean | 'indeterminate'
  onToggleSelectAll: () => unknown
  headerName: string
}

export function OCRScopingJobsListHeaderItemColumns({
  showCheckbox,
  checked,
  onToggleSelectAll,
  headerName,
}: OCRScopingJobsListHeaderItemColumnsProps) {
  return (
    <div className={styles.listHeader}>
      <span className={styles.listHeaderAction}>
        {showCheckbox && (
          <Checkbox checked={checked} onChange={onToggleSelectAll} />
        )}
      </span>
      <span>{headerName}</span>
    </div>
  )
}

export function OCRScopingJobsListHeaderStatusColumns() {
  return <div className={styles.aggregationListHeader}>Status</div>
}

export interface OCRScopingJobsListHeaderStatusItemProps {
  isDisabled: boolean
  count: number
  title: string
  subtitle: string
  onPress: () => unknown
}

export function OCRScopingJobsListHeaderStatusItem({
  isDisabled,
  count,
  title,
  subtitle,
  onPress,
}: OCRScopingJobsListHeaderStatusItemProps) {
  return (
    <SplitViewSidebarListItem
      isDisabled={isDisabled}
      onPress={onPress}
      extras={<OCRScopingJobsListHeaderCount count={count} />}
    >
      <SplitViewSidebarListItemTitle>{title}</SplitViewSidebarListItemTitle>
      <SplitViewSidebarListItemSubtitle>
        {subtitle}
      </SplitViewSidebarListItemSubtitle>
    </SplitViewSidebarListItem>
  )
}

interface OCRScopingJobsListHeaderCountProps {
  count: number
}

function OCRScopingJobsListHeaderCount({
  count,
}: OCRScopingJobsListHeaderCountProps) {
  return (
    <div className={styles.listCounter}>
      <span>{count}</span>
      <SplitViewSidebarListItemArrow />
    </div>
  )
}
